import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useSaveShortcuts,
  useSaveShortcutsEnabled,
  useShortcutBindings,
  useShortcutsEnabled,
} from '@/entities/viewer';
import { useErrorMessage } from '@/shared/i18n';
import {
  type Binding,
  DEFAULT_BINDINGS,
  findConflict,
  MAX_COMBOS,
  recordFromEvent,
  SHORTCUT_ACTIONS,
  type ShortcutAction,
  type ShortcutBindings,
  sameBinding,
  toOverrides,
} from '@/shared/lib/shortcuts';
import { Button, ConfirmDialog, ShortcutKeys } from '@/shared/ui';

import styles from './ShortcutSettings.module.css';

/** A single press commits after this pause, unless a second press extends it to a sequence. */
const SEQUENCE_MS = 900;

/**
 * Settings -> Keyboard shortcuts: change / disable / reset every configurable action. While an
 * action is being recorded the keys are captured here (capture phase, default prevented), so the
 * pressed shortcut never runs anything. Conflicts are rejected, never silently resolved.
 */
export function ShortcutSettings() {
  const { t } = useTranslation();
  const getErrorMessage = useErrorMessage();
  const bindings = useShortcutBindings();
  const save = useSaveShortcuts();
  const enabled = useShortcutsEnabled();
  const saveEnabled = useSaveShortcutsEnabled();
  const [wanted, setWanted] = useState<boolean | null>(null);
  const [recording, setRecording] = useState<ShortcutAction | null>(null);
  const [captured, setCaptured] = useState<string[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const latest = useRef({ bindings, recording, captured });
  useEffect(() => {
    latest.current = { bindings, recording, captured };
  });

  const apply = (action: ShortcutAction, binding: Binding): boolean => {
    const conflict = findConflict(bindings, action, binding);
    if (conflict) {
      setProblem(t('shortcuts.settings.conflict', { action: t(`shortcuts.action.${conflict}`) }));
      return false;
    }
    setProblem(null);
    const next: ShortcutBindings = { ...bindings, [action]: binding };
    save.mutate(toOverrides(next));
    return true;
  };

  const stop = () => {
    window.clearTimeout(timer.current);
    setRecording(null);
    setCaptured([]);
  };

  useEffect(() => {
    if (recording === null) return;
    const commit = (combos: string[]) => {
      const action = latest.current.recording;
      if (action === null) return;
      const ok = apply(action, combos);
      if (ok) stop();
      else {
        // Keep the recorder open so another combination can be pressed.
        window.clearTimeout(timer.current);
        setCaptured([]);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.isComposing) return;
      event.preventDefault();
      event.stopPropagation();
      const result = recordFromEvent(event);
      if (result.kind === 'modifier') return;
      if (result.kind === 'cancel') {
        setProblem(null);
        stop();
        return;
      }
      if (result.kind === 'rejected') {
        setProblem(t(`shortcuts.settings.${result.reason}`));
        window.clearTimeout(timer.current);
        setCaptured([]);
        return;
      }
      window.clearTimeout(timer.current);
      const next = [...latest.current.captured, result.combo].slice(0, MAX_COMBOS);
      setProblem(null);
      setCaptured(next);
      const bare = !result.combo.includes('+');
      if (next.length >= MAX_COMBOS || !bare) commit(next);
      else {
        timer.current = window.setTimeout(() => {
          commit(next);
        }, SEQUENCE_MS);
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      window.clearTimeout(timer.current);
    };
    // `apply` and `stop` only read the refs / current props; re-subscribing per render is not needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording]);

  return (
    <section className={styles.root} aria-labelledby="shortcuts-title">
      <div className={styles.head}>
        <h1 id="shortcuts-title" className={styles.title}>
          {t('shortcuts.settings.title')}
        </h1>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setConfirmAll(true);
          }}
          disabled={
            save.isPending ||
            SHORTCUT_ACTIONS.every(({ id }) => sameBinding(bindings[id], DEFAULT_BINDINGS[id]))
          }
        >
          {t('shortcuts.settings.resetAll')}
        </Button>
      </div>
      <label className={styles.master}>
        <input
          type="checkbox"
          role="switch"
          checked={wanted ?? enabled}
          onChange={(event) => {
            // Shown at once; the account value takes over when the request settles.
            setWanted(event.target.checked);
            saveEnabled.mutate(event.target.checked, {
              onSettled: () => {
                setWanted(null);
              },
            });
          }}
        />
        <span>
          <strong>{t('shortcuts.settings.enabled')}</strong>
          <span className={styles.note}>{t('shortcuts.settings.enabledNote')}</span>
        </span>
      </label>
      {!enabled && (
        <p role="status" className={styles.note}>
          {t('shortcuts.settings.disabledNotice')}
        </p>
      )}
      <p className={styles.note}>{t('shortcuts.settings.note')}</p>
      <ul className={`${styles.list} ${enabled ? '' : styles.inactive}`}>
        {SHORTCUT_ACTIONS.map(({ id }) => {
          const binding = bindings[id];
          const isRecording = recording === id;
          const isDefault = sameBinding(binding, DEFAULT_BINDINGS[id]);
          return (
            <li key={id} className={styles.row} data-testid={`shortcut-${id}`}>
              <span className={styles.name}>{t(`shortcuts.action.${id}`)}</span>
              <span className={styles.value}>
                {isRecording ? (
                  <span role="status" className={styles.recording}>
                    {captured.length > 0
                      ? t('shortcuts.settings.recordingMore')
                      : t('shortcuts.settings.recording')}
                  </span>
                ) : (
                  <ShortcutKeys
                    binding={binding}
                    emptyLabel={t('shortcuts.unassigned')}
                    thenLabel={t('shortcuts.then')}
                  />
                )}
              </span>
              <span className={styles.actions}>
                <Button
                  size="sm"
                  onClick={() => {
                    if (isRecording) {
                      stop();
                    } else {
                      setProblem(null);
                      setCaptured([]);
                      setRecording(id);
                    }
                  }}
                  aria-label={`${isRecording ? t('common.cancel') : t('shortcuts.settings.change')}: ${t(`shortcuts.action.${id}`)}`}
                >
                  {isRecording ? t('common.cancel') : t('shortcuts.settings.change')}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={binding.length === 0 || save.isPending}
                  onClick={() => {
                    if (recording) stop();
                    apply(id, []);
                  }}
                  aria-label={`${t('shortcuts.settings.disable')}: ${t(`shortcuts.action.${id}`)}`}
                >
                  {t('shortcuts.settings.disable')}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={isDefault || save.isPending}
                  onClick={() => {
                    if (recording) stop();
                    apply(id, DEFAULT_BINDINGS[id]);
                  }}
                  aria-label={`${t('shortcuts.settings.reset')}: ${t(`shortcuts.action.${id}`)}`}
                >
                  {t('shortcuts.settings.reset')}
                </Button>
              </span>
            </li>
          );
        })}
        <li className={styles.fixed}>
          <span className={styles.name}>{t('shortcuts.action.close')}</span>
          <span className={styles.value}>
            <kbd className={styles.esc}>Esc</kbd>
          </span>
          <span className={styles.actions}>
            <span className={styles.note}>{t('shortcuts.settings.fixed')}</span>
          </span>
        </li>
      </ul>
      {problem && (
        <p role="alert" className={styles.problem}>
          {problem}
        </p>
      )}
      {saveEnabled.isError && (
        <p role="alert" className={styles.problem}>
          {getErrorMessage(saveEnabled.error)}
        </p>
      )}
      {save.isError && (
        <p role="alert" className={styles.problem}>
          {getErrorMessage(save.error)}
        </p>
      )}
      {confirmAll && (
        <ConfirmDialog
          title={t('shortcuts.settings.resetAll')}
          message={t('shortcuts.settings.resetAllMessage')}
          confirmLabel={t('shortcuts.settings.resetAll')}
          confirmVariant="primary"
          pending={save.isPending}
          onCancel={() => {
            setConfirmAll(false);
          }}
          onConfirm={() => {
            setProblem(null);
            save.mutate(
              {},
              {
                onSuccess: () => {
                  setConfirmAll(false);
                },
              },
            );
          }}
        />
      )}
    </section>
  );
}
